<template>
  <div
    :id="`id_${mutableAnalysis.id}`"
    class="card"
  >
    <!-- HEAD OF THE CARD -->
    <div
      :style="{ backgroundColor: cardColorByType }"
      class="cardHeader"
    >
      <div class="cardHeaderTitle">
        {{ normTitle }}
      </div>
      <v-btn
        v-if="isCalculatedAnalysisInvalid"
        :color="cardColorByType"
        xSmall
        fab
        rounded
      >
        <v-icon>
          mdi-exclamation
        </v-icon>
      </v-btn>
      <v-toolbar-items>
        <v-menu offsetY>
          <template #activator="{ on }">
            <div class="d-flex align-center cardHeaderIcon">
              <v-btn
                class="calculated"
                color="primary"
                xSmall
                fab
                rounded
                :disabled="!analysis.active"
                :data-cy="analysisName"
                v-on="on"
              >
                <v-icon>
                  mdi-dots-vertical
                </v-icon>
              </v-btn>
            </div>
          </template>
          <!-- ACTION By Analysis types -->
          <v-list class="list">
            <!-- Calculated Analysis -->
            <ListItem
              v-if="analysisType === CALCULATED_ANALYSIS_TYPE"
              class="define-variable"
              icon="mdi-calculator-variant-outline"
              :title="$t('analyses.defineVariable')"
              @click="showModal"
            />
            <!-- History -->
            <ListItem
              icon="mdi-history"
              :title="$t('history.pageName')"
              @click="redirect"
            />
            <!-- Probe -->
            <ListItem
              v-if="notWaterProductFuelAnalysisType && (!mutableAnalysis.probe || !mutableAnalysis.probe.probeId)"
              icon="mdi-remote"
              :title="$t('generic.probeId')"
              @click="addProbe"
            />
            <!-- FLowMeters linking only for device reading analysis type and if no meter is present -->
            <ListItem
              v-if="analysisType === WATER_CONSUMPTION_ANALYSIS_TYPE && !analysis.flowMeter"
              icon="mdi-access-point-plus"
              :title="$t('devices.flowMeters.link')"
              @click="addFlowMeter"
            />
            <!-- FLowMeters Unlinking only for device reading analysis type and if meter is present -->
            <ListItem
              v-if="analysisType === WATER_CONSUMPTION_ANALYSIS_TYPE && analysis.flowMeter"
              icon="mdi-access-point-minus"
              :title="$t('devices.flowMeters.unlink')"
              @click="unlinkFlowMeter"
            />
            <!-- ProductLevel linking only for device reading analysis type -->
            <ListItem
              v-if="analysisType === PRODUCT_CONSUMPTION_ANALYSIS_TYPE && !analysis.productMeter"
              icon="mdi-access-point-plus"
              :title="$t('devices.productSensorHubs.link')"
              @click="addProductLevel"
            />
            <!-- ProductLevel Unlinking only for device reading analysis type-->
            <ListItem
              v-if="analysisType === PRODUCT_CONSUMPTION_ANALYSIS_TYPE && analysisType !== WATER_CONSUMPTION_ANALYSIS_TYPE && analysis.productMeter"
              icon="mdi-access-point-minus"
              :title="$t('devices.productSensorHubs.unlink')"
              @click="unlinkProductLevel"
            />
            <ListItem
              v-if="notWaterProductFuelAnalysisType && mutableAnalysis.probe && mutableAnalysis.probe.probeId"
              icon="mdi-remote-off"
              :title="$t('generic.unlinkprobeId')"
              @click="unlinkProbe"
            />
            <ListItem
              v-if="analysisType === FUEL_CONSUMPTION_ANALYSIS_TYPE && (!analysis.fuelMeter || isObjectEmpty(analysis.fuelMeter))"
              icon="mdi-remote"
              :title="$t('generic.linkFuelMeter')"
              @click="linkFuelMeter"
            />
            <ListItem
              v-if="analysisType === FUEL_CONSUMPTION_ANALYSIS_TYPE && analysis.fuelMeter && !isObjectEmpty(analysis.fuelMeter)"
              icon="mdi-remote"
              :title="$t('generic.unlinkFuelMeter')"
              @click="unlinkFuelMeter"
            />
            <!-- Work order selection -->
            <ListItem
              icon="mdi-format-list-bulleted"
              :title="$t('generic.workOrderSelection')"
              @click="addWorkOrderTypes"
            />
            <!-- Edit 'Manual flowmeter name' -->
            <ListItem
              v-if="(analysisType === WATER_CONSUMPTION_ANALYSIS_TYPE) && (analysis.flowMeter
                && [SOURCE_MANUAL, SOURCE_CONTROLLER].includes(analysis.flowMeter.dataSource))"
              icon="mdi-pencil"
              :title="$t('devices.flowMeters.edit')"
              @click="onEditManualFlowmeterAnalysis"
            />
            <!-- Edit Manual productLevel name only and show iot, controller -->
            <ListItem
              v-if="(analysisType === PRODUCT_CONSUMPTION_ANALYSIS_TYPE) && (analysis.productMeter)"
              icon="mdi-pencil"
              :title="$t('forms.btn.edit')"
              @click="onEditProductLevelLink"
            />
            <!-- Delete -->
            <ListItem
              icon="mdi-delete-outline"
              color="red"
              :title="$t('generic.delete')"
              @click="confirmRemoveAnalysis"
            />
          </v-list>
        </v-menu>
      </v-toolbar-items>
    </div>
    <!-- CONTENT OF THE CARD -->
    <div
      v-show="showContent"
      class="cardContent"
    >
      <DefineVariable
        v-if="isModalVisible"
        v-show="isModalVisible"
        :analysis="analysis"
        @update="updateAnalysis"
        @close="closeModal"
      />
      <!-- CARD CONTENT -->
      <div class="cardContentBody">
        <!-- LEFT PART or the card body -->
        <div
          class="cardContentBodyLeft"
        >
          <!-- analysis Type icon  -->
          <TreeCardIcon
            :src="getIconProps(analysisType).src"
            :alt="getIconProps(analysisType).alt"
            showBorder
          />
          <div>
            <!-- analysis content & references -->
            <VSelect
              v-if="notWaterProductFuelAnalysisType"
              v-model="selectedStandardFamily"
              :label="$t('forms.labels.standardFamily')"
              extraClass="siteTree-standard"
              hideDetails
              :items="availableStandardFamilies"
              :loading="availableStandardsLoading"
              :disabled="!analysis.active || !hasPermission('analyses.configurations.edit')"
            />
            <!-- If it is a water consumption analysis type (flow meters) -->
            <div
              v-else-if="analysisType === WATER_CONSUMPTION_ANALYSIS_TYPE"
            >
              <!-- if it's an IOT meter -->
              <div v-if="analysis.flowMeter && [analysis.flowMeter.type, analysis.flowMeter.dataSource].includes(SOURCE_IOT)">
                <div v-if="analysis.flowMeterHub">
                  {{ analysis.flowMeterHub.name }}
                </div>
                <div class="bold-text">
                  {{ analysis.flowMeter.name }}
                </div>
              </div>
              <!-- if it's an CONTROLLER meter -->
              <div v-if="analysis.flowMeter && analysis.flowMeter.dataSource === SOURCE_CONTROLLER">
                <div v-if="analysis.controller">
                  {{ analysis.controller.name }}
                </div>
                <div class="bold-text">
                  {{ analysis.flowMeter.name }}
                </div>
              </div>
              <!-- if it's an manual meter -->
              <div v-else-if="analysis.flowMeter && analysis.flowMeter.dataSource === SOURCE_MANUAL">
                <div class="bold-text">
                  {{ analysis.flowMeter.name }}
                </div>
              </div>
            </div>
            <!-- If it is a product level analysis type (product level)-->
            <div
              v-else-if="analysisType === PRODUCT_CONSUMPTION_ANALYSIS_TYPE"
            >
              <!-- if it's an IOT product level -->
              <div v-if="analysis.productMeter && analysis.productMeter.dataSource === SOURCE_IOT">
                <div>
                  {{ analysis.productMeter.name }}
                </div>
                <div class="bold-text">
                  {{ analysis.productMeter.siteProduct.product.name }}
                </div>
              </div>
              <!-- if it's an CONTROLLER product level -->
              <div v-if="analysis.productMeter && analysis.productMeter.dataSource === SOURCE_CONTROLLER">
                <div v-if="analysis.controller">
                  {{ analysis.controller.name }}
                </div>
                <div class="bold-text">
                  {{ productNameByProductMeter }}
                </div>
              </div>
              <!-- if it's an manual product level -->
              <div
                v-else-if="analysis.productMeter && analysis.productMeter.dataSource === SOURCE_MANUAL"
              >
                <div>
                  {{ analysis.productMeter.name }}
                </div>
                <div class="bold-text">
                  {{ analysis.productMeter.siteProduct.product.name }}
                </div>
              </div>
            </div>
            <div
              v-else-if="analysisType === FUEL_CONSUMPTION_ANALYSIS_TYPE"
            >
              <!-- if it's an IOT meter -->
              <div v-if="analysis.fuelMeter && analysis.fuelMeter.dataSource === SOURCE_IOT">
                <div v-if="analysis.flowMeterHub">
                  {{ analysis.flowMeterHub.name }}
                </div>
                <div
                  v-if="analysis.fuelMeter"
                  class="bold-text"
                >
                  {{ analysis.fuelMeter.fuelMeterName }}
                </div>
              </div>
              <!-- if it's an CONTROLLER meter -->
              <div v-if="analysis.fuelMeter && analysis.fuelMeter.dataSource === SOURCE_CONTROLLER">
                <div v-if="analysis.controller">
                  {{ analysis.controller.name }}
                </div>
                <div class="bold-text">
                  {{ analysis.fuelMeter.fuelMeterName }}
                </div>
              </div>
              <!-- if it's an manual meter -->
              <div v-else-if="analysis.fuelMeter && analysis.fuelMeter.dataSource === SOURCE_MANUAL">
                <div class="bold-text">
                  {{ analysis.fuelMeter.fuelMeterName }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- RIGHT PART or the card body -->
        <div class="cardContentBodyRight">
          <div
            v-if="probeId && !analysis.flowMeter"
          >
            <v-icon>mdi-remote</v-icon>
            <div>
              {{ probeId }}
            </div>
          </div>
          <div v-if="analysis.fuelMeter && [SOURCE_IOT, SOURCE_MANUAL].includes(analysis.fuelMeter.dataSource)">
            <v-icon
              v-if="analysis.fuelMeter.dataSource === SOURCE_MANUAL"
            >
              mdi-account-edit
            </v-icon>
            <div>
              <div
                v-if="analysis.fuelMeter.dataSource === SOURCE_MANUAL"
                class="font-weight-regular"
              >
                {{ this.$t('devices.userInput') }}
              </div>
            </div>
          </div>
          <div
            v-if="analysis.productMeter && [SOURCE_IOT, SOURCE_MANUAL].includes(analysis.productMeter.dataSource)"
          >
            <v-icon
              v-if="analysis.productMeter.dataSource === SOURCE_MANUAL"
            >
              mdi-account-edit
            </v-icon>
            <v-icon
              v-if="analysis.productMeter.dataSource === SOURCE_IOT"
            >
              mdi-memory
            </v-icon>
            <div>
              <!-- if it's an IOT ProductLevel -->
              <div
                v-if="analysis.productMeter.dataSource === SOURCE_IOT"
                class="font-weight-regular"
              >
                {{ analysis.productMeter.macAddress }}
              </div>
              <!-- if it's an manual ProductLevel -->
              <div
                v-else-if="analysis.productMeter.dataSource === SOURCE_MANUAL"
                class="font-weight-regular"
              >
                {{ this.$t('devices.userInput') }}
              </div>
            </div>
          </div>
          <div
            v-if="analysis.flowMeter"
          >
            <v-icon
              v-if="analysis.flowMeter.dataSource === SOURCE_CONTROLLER"
            >
              mdi-remote
            </v-icon>

            <v-icon
              v-if="analysis.flowMeter.dataSource === SOURCE_MANUAL"
            >
              mdi-account-edit
            </v-icon>

            <v-icon
              v-if="[analysis.flowMeter.type, analysis.flowMeter.dataSource].includes(SOURCE_IOT)"
            >
              mdi-memory
            </v-icon>

            <div>
              <div>
                <!-- if it's an IOT meter -->
                <div
                  v-if="analysis.flowMeter && [analysis.flowMeter.type, analysis.flowMeter.dataSource].includes(SOURCE_IOT) && analysis.flowMeterHub"
                  class="font-weight-regular"
                >
                  {{ analysis.flowMeterHub.macAddress }}
                </div>
                <!-- if it's an CONTROLLER meter -->
                <div
                  v-if="analysis.flowMeter.dataSource === SOURCE_CONTROLLER && analysis.controller && analysis.controller.probes"
                  class="font-weight-regular"
                >
                  {{ getProbeId(analysis) }}
                </div>
                <!-- if it's an manual meter -->
                <div
                  v-else-if="analysis.flowMeter.dataSource === SOURCE_MANUAL"
                  class="font-weight-regular"
                >
                  {{ this.$t('devices.userInput') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- FOOTER - Norm values OR Device Config-->
      <NormValues
        v-if="notWaterProductFuelAnalysisType"
        :normValues="norms"
        :disabled="!analysis.active || !selectedStandardFamily"
        :readonly="!hasPermission('analyses.configurations.edit')"
        :analysisName="analysis.analysisType.name[lang]"
        @focus="resetToCustom"
        @dirtyChanged="isNormsDirty = $event"
        @validChanged="isNormsValid = $event"
      />
      <FlowMeterDetails
        v-if="analysisType === WATER_CONSUMPTION_ANALYSIS_TYPE"
        :flowMeter="analysis.flowMeter"
      />
      <ProductLevelDetails
        v-if="analysisType === PRODUCT_CONSUMPTION_ANALYSIS_TYPE"
        :productLevel="analysis.productMeter"
      />
      <FuelMeterDetails
        v-if="analysisType === FUEL_CONSUMPTION_ANALYSIS_TYPE"
        :fuelMeter="analysis.fuelMeter"
      />

      <v-row
        align="center"
        justify="end"
        class="mr-1"
      >
        <v-btn
          v-if="isNormsDirty && isNormsValid && notWaterProductFuelAnalysisType && hasPermission('analyses.configurations.edit')"
          color="secondary"
          class="ml-1 siteTree-btn"
          @click="submitData"
        >
          {{ $t('forms.btn.save') }}
        </v-btn>
      </v-row>
    </div>
  </div>
</template>

<script>
import { isPlainObject, get, isEmpty } from 'lodash';
import { mapGetters } from 'vuex';
import {
    CALCULATED_ANALYSIS_TYPE,
    WATER_CONSUMPTION_ANALYSIS_TYPE,
    SIMPLE_ANALYSIS_TYPE,
    analysisConfiguration,
    PRODUCT_CONSUMPTION_ANALYSIS_TYPE,
    FUEL_CONSUMPTION_ANALYSIS_TYPE,
} from '@/aquassist-shared/constants/types/componentAnalysisConfiguration';
import { SOURCE_IOT, SOURCE_MANUAL, SOURCE_CONTROLLER } from '@/aquassist-shared/constants/types/flowMeters';
import FlowMeterDetails from '@/components/sites/TreeAnalysisCard/FlowMeterDetails.vue';
import Vue from 'vue';
import EventBus from '@/utilities/event-bus';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import NormValues from '@/components/sites/TreeAnalysisCard/NormValues';
import ListItem from '@/components/shared/widgets/ListItem.vue';
import AnalysisWorkOrderTypesForm from '@/components/component/AnalysisWorkOrderTypesForm';
import AddProbeForm from '@/components/component/AddProbeForm';
import LinkFlowMeterForm from '@/components/component/LinkFlowMeterForm.vue';
import FlowMeterMixin from '@/mixins/FlowMeterMixin';
import LinkProductLevelForm from '@/components/component/LinkProductLevelForm.vue';
import LinkFuelForm from '@/components/component/LinkFuelForm.vue';
import TreeCardIcon from '@/components/component/icons/TreeCardIcon.vue';
import ProductLevelDetails from '@/components/sites/TreeAnalysisCard/ProductLevelDetails.vue';
import FuelMeterDetails from './FuelMeterDetails.vue';
import DefineVariable from './DefineVariable.vue';


const DEFFAULT_NORM_VALUES = {
    veryLow: undefined,
    low: undefined,
    minimum: undefined,
    maximum: undefined,
    high: undefined,
    veryHigh: undefined,
};

export default {
    components: {
        ProductLevelDetails,
        TreeCardIcon,
        FlowMeterDetails,
        VSelect,
        NormValues,
        ListItem,
        DefineVariable,
        FuelMeterDetails,
    },
    mixins: [
        FlowMeterMixin,
    ],
    props: {
        analysis: {
            type: Object,
            default: () => {
            },
        },
        siteId: {
            type: String,
            required: true,
        },
        typeOfComponent: {
            type: String,
            default: null,
            required: false,
        },
    },
    data() {
        return {
            mutableAnalysis: this.analysis,
            customStandardFamily: null,
            showContent: true,
            customValues: null,
            availableStandardsPerStandardFamily: null,
            availableStandardsPerId: null,
            availableStandardFamilies: [],
            availableStandards: [],
            availableStandardsLoading: false,
            norms: { ...DEFFAULT_NORM_VALUES },
            selectedStandardFamily: null,
            selectedStandard: null,
            haveAvailableStandardsBeenLoaded: false,
            templateType: null, // can be custom or standard
            isNormsDirty: false,
            isNormsValid: false,
            probeId: null,
            isModalVisible: false,
        };
    },
    computed: {
        PRODUCT_CONSUMPTION_ANALYSIS_TYPE() {
            return PRODUCT_CONSUMPTION_ANALYSIS_TYPE;
        },
        SOURCE_MANUAL() {
            return SOURCE_MANUAL;
        },
        SOURCE_IOT() {
            return SOURCE_IOT;
        },
        SOURCE_CONTROLLER() {
            return SOURCE_CONTROLLER;
        },
        FUEL_CONSUMPTION_ANALYSIS_TYPE() {
            return FUEL_CONSUMPTION_ANALYSIS_TYPE;
        },
        ...mapGetters([
            'siteLang',
            'lang',
            'teamMainLang',
            'hasPermission',
        ]),
        notWaterProductFuelAnalysisType() {
            return this.analysisType !== WATER_CONSUMPTION_ANALYSIS_TYPE
            && this.analysisType !== PRODUCT_CONSUMPTION_ANALYSIS_TYPE
            && this.analysisType !== FUEL_CONSUMPTION_ANALYSIS_TYPE;
        },
        iconArrow() {
            return (this.showContent) ? 'keyboard_arrow_down' : 'keyboard_arrow_right';
        },
        analysisType() {
            return this.mutableAnalysis.analysisType.type;
        },
        /**
         * Gets the default color for a card based on the analysis type.
         *
         * @returns {string} The default color for the card.
         */
        cardColorByType() {
            const { type } = this.mutableAnalysis.analysisType;
            return analysisConfiguration[type]?.color || 'primary';
        },

        normTitle() {
            const {
                name = {},
                measureUnit,
                reportUnitOfMeasure,
                type,
            } = this.mutableAnalysis.analysisType;
            let unit = '';
            if (reportUnitOfMeasure) {
                unit = reportUnitOfMeasure;
            } else if (isPlainObject(measureUnit)) {
                unit = measureUnit[this.lang] || measureUnit[this.teamMainLang];
            }
            const displayName = name[this.lang] || name[this.teamMainLang];
            // If no units are defined, we display the generic (units)
            if (!unit || unit === 'undefined' || unit === 'null') {
                unit = this.$t('generic.units');
            }
            return type === WATER_CONSUMPTION_ANALYSIS_TYPE
            || type === PRODUCT_CONSUMPTION_ANALYSIS_TYPE
            || type === FUEL_CONSUMPTION_ANALYSIS_TYPE
                ? `${displayName}`
                : `${displayName} (${unit})`;
        },

        isCalculatedAnalysisInvalid() {
            if (!this.isCalculated) return false;

            if (!this.mutableAnalysis.calculatedAnalysisParams) return false;

            return this.mutableAnalysis.calculatedAnalysisParams.variables.length < 1;
        },

        analysisName() {
            const { name = {} } = this.mutableAnalysis.analysisType;
            return `${name[this.lang]}`;
        },
        CALCULATED_ANALYSIS_TYPE() {
            return CALCULATED_ANALYSIS_TYPE;
        },
        WATER_CONSUMPTION_ANALYSIS_TYPE() {
            return WATER_CONSUMPTION_ANALYSIS_TYPE;
        },
        productNameByProductMeter() {
            const pm = this.analysis?.productMeter;
            return pm && pm.siteProduct && pm.siteProduct.product ? pm.siteProduct.product.name : '';
        },
    },
    watch: {
        selectedStandardFamily(newVal) {
            this.isNormsDirty = true;
            if (this.templateType === 'custom' && newVal !== this.customStandardFamily.id) {
                this.templateType = 'standard';
                this.stringifyNorms(DEFFAULT_NORM_VALUES);
            } else if (newVal === this.customStandardFamily.id) {
                this.resetToCustom();
            }
            if (newVal
                && this.availableStandardsPerStandardFamily
                && this.availableStandardsPerStandardFamily[newVal]
                && newVal !== this.customStandardFamily.id) {
                this.selectedStandard = this.availableStandardsPerStandardFamily[newVal][0].id;
            }
        },
        selectedStandard(newVal) {
            if (newVal !== null) {
                this.stringifyNorms(this.availableStandardsPerId[newVal].norm);
            }
        },
        'mutableAnalysis.template': {
            handler() {
                this.customStandardFamily = {
                    id: 'custom',
                    name: this.$t('standardFamilies.labels.custom'),
                };
                this.availableStandardFamilies.push(this.customStandardFamily);

                if (this.mutableAnalysis.template.type === 'custom') {
                    this.selectedStandardFamily = this.customStandardFamily.id;
                    this.selectedStandard = null;
                    this.templateType = 'custom';
                    this.customValues = this.mutableAnalysis.norm.id;
                    this.stringifyNorms(this.mutableAnalysis.norm);
                } else {
                    if (!this.haveAvailableStandardsBeenLoaded) {
                        this.availableStandards = [this.mutableAnalysis.template.standardDeepCopy];
                    }
                    this.formatAvailableStandards();
                    this.selectedStandard = this.mutableAnalysis?.template?.standardDeepCopy?.id;
                    this.selectedStandardFamily = this.mutableAnalysis?.template?.standardDeepCopy?.standardFamily?.id;
                    this.templateType = 'standard';
                }
            },
            deep: true,
            immediate: true,
        },
    },
    async created() {
        await this.fetchAvailableStandards();
    },
    methods: {
        getProbeId(analysis) {
            const id = '_id';
            const probe = analysis.controller.probes.find(h => h.device && h.device === analysis.flowMeter[id]);
            return probe?.probeId;
        },
        showModal() {
            this.isModalVisible = true;
        },
        closeModal() {
            this.isModalVisible = false;
        },
        /**
         * Returns the icon component props for the given analysis type.
         *
         * @param {string} type - The type of analysis.
         * @returns {Object} - The icon component for the analysis type.
         */
        getIconProps(type) {
            const defaultProps = {
                src: '/assets/SimpleAnalysisType.svg',
                alt: 'Analysis',
            };
            switch (type) {
                case SIMPLE_ANALYSIS_TYPE:
                case CALCULATED_ANALYSIS_TYPE:
                    return defaultProps;
                case WATER_CONSUMPTION_ANALYSIS_TYPE:
                    return {
                        src: '/assets/FlowMeterHubBlack.svg',
                        alt: 'FlowMeterHub',
                    };
                case PRODUCT_CONSUMPTION_ANALYSIS_TYPE:
                    return {
                        src: '/assets/ProductMeterIcon.svg',
                        alt: 'ProductMeter',
                    };
                case FUEL_CONSUMPTION_ANALYSIS_TYPE:
                    return {
                        src: '/assets/fuelMeterBlack.svg',
                        alt: 'FuelMeter',
                    };
                // eslint-disable-next-line no-fallthrough
                default:
                    return defaultProps;
            }
        },

        async fetchAvailableStandards() {
            if (!this.haveAvailableStandardsBeenLoaded) {
                this.probeId = this.mutableAnalysis.probe?.probeId;
                this.availableStandards = this.mutableAnalysis.availableStandards;
                this.formatAvailableStandards();
                this.availableStandardsLoading = false;
                this.haveAvailableStandardsBeenLoaded = true;
                if (this.templateType === 'standard' && this.selectedStandardFamily && this.availableStandardsPerId[this.selectedStandardFamily]) {
                    this.selectedStandard = this.availableStandardsPerId[this.selectedStandardFamily][0].id;
                }
            }
        },
        // As the available standards returned from backend are stored in an array,
        // We need to build some more suited data structures to allow the user to choose first a standard family and then one of its standard.
        // We also need to format the name of each standard (with its network/component type and priority score),
        // whether a standard family is a team/client default.
        // - availableStandardsPerStandardFamily : is a dictionary of standards array stored by standard family id
        // - availableStandardsPerId : is a dictionary of every standard stored by their ids.
        // - availableStandardFamilies : is an array of every available standard families
        formatAvailableStandards() {
            this.availableStandardsPerStandardFamily = this.availableStandards.reduce((output, availableStandard) => {
                const result = { ...output };
                const standard = { ...availableStandard };

                if (!result[standard.standardFamily.id]) {
                    result[standard.standardFamily.id] = [];
                }
                if (!standard.isFormatted) {
                    if (standard.networkType && standard.componentType) {
                        standard.name = `${this.$t('standards.labels.componentType')} : ${standard.componentType.name} -
                            ${this.$t('standards.labels.networkType')} : ${standard.networkType.name}`;
                    } else if (standard.componentType) {
                        standard.name = `${this.$t('standards.labels.componentType')} : ${standard.componentType.name} -
                            ${this.$t('standards.labels.networkType')} : Any`;
                    } else if (standard.networkType) {
                        standard.name = `${this.$t('standards.labels.componentType')} : Any -
                            ${this.$t('standards.labels.networkType')} : ${standard.networkType.name}`;
                    } else {
                        standard.name = `${this.$t('standards.labels.componentType')} : Any -
                            ${this.$t('standards.labels.networkType')} : Any`;
                    }
                    if (standard.priorityScore) {
                        standard.name += ` - ${this.$t('standards.labels.priority')} ${standard.priorityScore}%`;
                    }
                    standard.isFormatted = true;
                }
                result[standard.standardFamily.id].push(standard);
                return result;
            }, {});

            this.availableStandardFamilies = [];
            this.availableStandardFamilies.push(this.customStandardFamily);
            this.availableStandardsPerId = {};

            this.availableStandards.forEach(standard => {
                const family = { ...standard.standardFamily };

                this.availableStandardsPerId[standard.id] = standard;
                if (!standard.standardFamily.isFormatted) {
                    if (standard.standardFamily.isClientDefaultStandard) {
                        family.name += ` - ${this.$t('standardFamilies.labels.isClientDefault')}`;
                    } else if (standard.standardFamily.isTeamDefaultStandard) {
                        family.name += ` - ${this.$t('standardFamilies.labels.isTeamDefault')}`;
                    }
                    family.isFormatted = true;
                }
                this.availableStandardFamilies.push(family);
            });
        },
        confirmRemoveAnalysis() {
            let messagePath = 'analyses.confirmRemove';
            const analysisTypeMessage = get(this.mutableAnalysis, `analysisType.name.${this.lang}`);
            // If the card linked to a device
            if ((this.mutableAnalysis?.prob && !isEmpty(this.mutableAnalysis?.prob))
            || (this.mutableAnalysis?.flowMeter && !isEmpty(this.mutableAnalysis?.flowMeter))
            || (this.mutableAnalysis?.productMeter && !isEmpty(this.mutableAnalysis?.productMeter))
            || (this.mutableAnalysis?.fuelMeter && !isEmpty(this.mutableAnalysis?.fuelMeter))) {
                messagePath = 'analyses.confirmRemoveLinkedCard';
            }

            this.$root.$emit('showModal', {
                title: this.$t('forms.btn.removeAnalysis'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: { msg: this.$t(messagePath, { data: analysisTypeMessage }) },
                onConfirm: () => this.removeAnalysis(),
                isForm: false,
            });
        },
        onEditManualFlowmeterAnalysis() {
            this.$root.$emit('showModal', {
                title: this.$t('devices.flowMeters.edit'),
                payload: { meter: this.analysis, editMode: true },
                component: Vue.component('LinkFlowMeterForm', LinkFlowMeterForm),
                onConfirm: async requestPayload => {
                    if (!requestPayload) return;
                    await this.$rq.updateFlowMeter(
                        this.siteId,
                        // eslint-disable-next-line no-underscore-dangle
                        requestPayload._id,
                        { name: requestPayload.name, unitsOfMeasure: requestPayload.unitsOfMeasure, isUpdateLink: true },
                    );
                    this.$toasted.global.toast_success({
                        message: this.$t('generic.flowmeterLinkUpdated'),
                    });
                    this.$emit('reloadAnalysis');
                },
                controlledValidation: true,
                isForm: true,
                width: 600,
            });
        },
        onEditProductLevelLink() {
            if (!this.analysis.productMeter) return;
            this.$root.$emit('showModal', {
                title: this.$t('devices.productLevel.edit'),
                payload: {
                    productMeterSelected: this.analysis,
                    component: this.analysis.component,
                    editMode: true,
                },
                component: Vue.component('LinkProductLevelForm', LinkProductLevelForm),
                onConfirm: async requestPayload => {
                    if (!requestPayload) return;
                    const payload = {
                        productLevelName: requestPayload.productLevelName,
                        lowLevelMeasureUnits: requestPayload.lowLevelMeasureUnits,
                        maxLevelMeasureUnits: requestPayload.maxLevelMeasureUnits,
                        reportMeasureUnits: requestPayload.reportMeasureUnits,
                        readingMeasureUnits: requestPayload.readingMeasureUnits,
                        productContainer: requestPayload.productContainer,
                        productContainerCustom: requestPayload.productContainerCustom,
                        report: requestPayload.report,
                        reading: requestPayload.reading,
                        maxLevel: requestPayload.maxLevel,
                        lowLevel: requestPayload.lowLevel,
                        isLinkEdit: true,
                    };
                    await this.$rq.editProductSensorsHub(this.siteId, this.analysis.productMeter.id, payload);
                    this.$toasted.global.toast_success({
                        message: this.$t('generic.productLevelLinkUpdated'),
                    });
                    this.$emit('reloadAnalysis');
                },
                controlledValidation: true,
                isForm: true,
                width: 600,
            });
        },
        stringifyNorms(analysisNorm) {
            this.norms = Object.assign(this.norms, Object.keys(analysisNorm)
                .reduce((stringifiedNorms, normKey) => {
                    if (!Object.keys(this.norms)
                        .includes(normKey)) {
                        return stringifiedNorms;
                    }

                    function stringifyNorm(value) {
                        if (analysisNorm[normKey] !== '0' && analysisNorm[normKey] !== null && analysisNorm[normKey] !== undefined) {
                            return value.toString();
                        }
                        if (value === '0') {
                            return '0';
                        }
                        return '';
                    }

                    return {
                        ...stringifiedNorms,
                        [normKey]: stringifyNorm(analysisNorm[normKey]),
                    };
                }, {}));
        },
        removeAnalysis() {
            EventBus.$emit('node-action', ['removeConfiguration', {
                componentId: this.mutableAnalysis.component.id,
                configurationId: this.mutableAnalysis.id,
                configurationName: this.mutableAnalysis.analysisType.name,
                siteId: this.siteId,
            }]);
        },
        redirect() {
            this.$router.push({
                name: 'history',
                params: {
                    clientId: this.$route.params.clientId,
                    analysisTypeId: this.mutableAnalysis.analysisType.id,
                    componentId: this.mutableAnalysis.component.id,
                },
            });
        },
        async submitData() {
            const configurationInfo = { analysisType: this.mutableAnalysis.analysisType.id };
            if (this.templateType === 'custom') {
                configurationInfo.norm = this.$utils.removeEmptyNullOrUndefined(this.norms, true);
            } else {
                configurationInfo.standard = this.selectedStandard;
            }
            const { data } = await this.$rq.updateAnalysisConfig(this.mutableAnalysis.id, configurationInfo);
            if (this.templateType === 'custom') {
                this.customValues = data.norm;
            }
            this.mutableAnalysis = data;
            this.$toasted.global.toast_success({
                message: this.$t('sites.norms.updated', { user: data.name }),
            });
            this.isNormsValid = false;
        },
        resetToCustom() {
            if (this.templateType === 'standard') {
                this.selectedStandardFamily = this.customStandardFamily.id;
                this.selectedStandard = null;
                this.templateType = 'custom';
                if (this.templateType === 'custom') {
                    this.stringifyNorms(this.customValues || this.mutableAnalysis.norm);
                }
            }
            this.isNormsValid = true;
        },
        async addWorkOrderTypesToAnalysis(requestPayload) {
            if (!requestPayload) return;
            const workOrderTypes = { workOrderTypes: requestPayload.selectedWorkOrdertypes };
            await this.$rq.updateAnalysisConfig(this.mutableAnalysis.id, workOrderTypes);
        },
        /**
         * Adds a probe to the analysis configuration.
         *
         * @param {Object} requestPayload - The payload containing the data for adding the probe.
         * @param {string} requestPayload.probeId - The ID of the probe to be added.
         * @param {string} requestPayload.analysisId - The ID of the analysis.
         *
         * @return {Promise} - A promise that resolves when the probe is successfully added to the analysis configuration.
         */
        async addProbeToAnalysis(requestPayload) {
            if (!requestPayload) return;
            await this.$rq.updateAnalysisConfig(this.mutableAnalysis.id, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('generic.probeUpdated', { id: requestPayload.probeId }),
            });
        },
        /**
         * Adds a flow meter to the analysis configuration.
         *
         * @param {Object} requestPayload - The payload containing the request details.
         * @param {string} requestPayload.probeId - The ID of the probe to be updated.
         * @param {any} requestPayload.otherParams - Other parameters required for the update.
         * @returns {void}
         */
        async addFlowMeterToAnalysis(requestPayload) {
            await this.$rq.updateAnalysisConfig(this.mutableAnalysis.id, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('generic.probeUpdated', { id: requestPayload.probeId }),
            });
        },
        /**
         * Method to add a probe to the analysis.
         * Emits a 'showModal' event to display a modal with an 'AddProbeForm' component.
         * Once the form is submitted, it calls the 'addProbeToAnalysis' method to add the probe to the analysis.
         * If successful, emits a 'reloadAnalysis' event to reload the analysis.
         *
         * @async
         * @return {Promise<void>} A promise that resolves once the probe is added to the analysis.
         */
        async addProbe() {
            this.$root.$emit('showModal', {
                title: this.$t('generic.probeId'),
                payload: { analysisConfigurationId: this.analysis.id },
                component: Vue.component('AddProbeForm', AddProbeForm),
                onConfirm: async requestPayload => {
                    if (requestPayload) {
                        if (requestPayload.probeId && requestPayload.controllerId) {
                            await this.addProbeToAnalysis({
                                probe: requestPayload.probeId,
                                analysisType: this.analysis.analysisType.id,
                                component: this.analysis.component.id,
                                controller: requestPayload.controllerId,
                            });
                            this.$emit('reloadAnalysis');
                        }
                    }
                },
                isForm: false,
                width: 800,
            });
        },
        /**
         * Unlinks a probe from the analysis.
         *
         * @async
         * @function unlinkProbe
         * @returns {Promise<void>}
         * @description This method unlinks a probe from the analysis
         */
        async unlinkProbe() {
            await this.$rq.unlinkProbe(this.analysis.id);
            this.$toasted.global.toast_success({
                message: this.$t('generic.unlinkMsg'),
            });
            this.$emit('reloadAnalysis');
        },
        /**
         * Add a flow meter to the analysis configuration.
         *
         * @return {void}
         */
        async addFlowMeter() {
            this.$root.$emit('showModal', {
                title: this.$t('devices.flowMeters.link'),
                payload: { typeOfComponent: this.typeOfComponent, flowmeterType: this.analysis?.analysisType?.flowmeterType },
                component: Vue.component('LinkFlowMeterForm', LinkFlowMeterForm),
                onConfirm: async requestPayload => {
                    if (!requestPayload) return;
                    const payload = await this.createFlowMeter(requestPayload, this.analysis.component.id);
                    await this.addFlowMeterToAnalysis({
                        flowMeter: payload.flowMeter,
                        analysisType: this.analysis.analysisType.id,
                        component: this.analysis.component.id,
                        flowMeterHub: payload.flowMeterHub,
                        controller: payload.controller,
                        site: this.siteId,
                        type: payload.type,
                    });
                    this.$emit('reloadAnalysis');
                },
                isForm: false,
                width: 600,
            });
        },
        async addProductLevel() {
            this.$root.$emit('showModal', {
                title: this.$t('devices.productSensorHubs.link'),
                payload: {
                    analysisConfigurationId: this.analysis.id,
                    component: this.analysis.component,
                },
                component: Vue.component('LinkProductLevelForm', LinkProductLevelForm),
                onConfirm: async requestPayload => {
                    if (!requestPayload) return;
                    const payload = {
                        component: requestPayload.component,
                        analysisType: this.analysis.analysisType.id,
                        siteProduct: requestPayload.productName,
                        dataSource: requestPayload.dataSource,
                        readingValue: Number(requestPayload.reading),
                        readingUnits: requestPayload.readingMeasureUnits,
                        reportValue: Number(requestPayload.report),
                        reportUnits: requestPayload.reportMeasureUnits,
                        conversionFactor: 0.2, // Todo change this in the next story when conversion will happen
                        maxLevel: Number(requestPayload.maxLevel),
                        maxLevelUnits: requestPayload.maxLevelMeasureUnits,
                        minLevel: Number(requestPayload.lowLevel),
                        minLevelUnits: requestPayload.lowLevelMeasureUnits,
                        analysisConfigType: 'productMeter',
                        productContainer: requestPayload.productContainer,
                        productContainerCustom: requestPayload.productContainerCustom,

                    };
                    if (requestPayload.dataSource === SOURCE_IOT) {
                        payload.productMeter = requestPayload.productLevelHub;
                    }
                    if (requestPayload.dataSource === SOURCE_CONTROLLER) {
                        payload.probe = requestPayload.probe;
                        payload.controller = requestPayload.controller;
                    }
                    if (requestPayload.dataSource === SOURCE_MANUAL) {
                        payload.name = requestPayload.productLevelName;
                    }
                    await this.$rq.linkProductLevel(this.analysis.id, payload);
                    this.$emit('reloadAnalysis');
                },
                controlledValidation: true,
                isForm: true,
                width: 600,
            });
        },
        async linkFuelMeter() {
            this.$root.$emit('showModal', {
                title: this.$t('generic.linkFuelMeter'),
                component: Vue.component('LinkFuelForm', LinkFuelForm),
                payload: {
                    analysis: this?.analysis,
                },
                onConfirm: async requestPayload => {
                    if (!requestPayload) return;
                    const payload = this.$utils.removeEmptyNullOrUndefined(requestPayload, true);
                    const { data: fuelMeter } = await this.$rq.addFuelMeter(this.siteId, payload);
                    // link the fuel meter to the analysis
                    const updatePayload = {
                        site: this.siteId,
                        component: this.analysis.component.id,
                        analysisType: this.analysis.analysisType.id,
                        fuelMeter: fuelMeter.id,
                        controller: payload.controllerHub,
                        probeId: requestPayload.probeId,
                        probe: requestPayload.probe,
                    };
                    await this.$rq.updateAnalysisConfig(this.analysis.id, updatePayload);

                    this.$emit('reloadAnalysis');
                },

            });
        },
        /**
         * Unlink the flow meter from the analysis.
         *
         * @return {Promise<void>} A Promise that resolves when the flow meter is unlinked.
         */
        async unlinkFlowMeter() {
            await this.$rq.unlinkFlowMeter(this.analysis.id);
            this.$toasted.global.toast_success({
                message: this.$t('generic.unlinkMsg'),
            });
            this.$emit('reloadAnalysis');
        },
        async unlinkProductLevel() {
            await this.$rq.unlinkProductLevel(this.analysis.id);
            this.$toasted.global.toast_success({
                message: this.$t('generic.unlinkMsg'),
            });
            this.$emit('reloadAnalysis');
        },
        async unlinkFuelMeter() {
            await this.$rq.unlinkFuelMeter(this.analysis.id);
            this.$toasted.global.toast_success({
                message: this.$t('generic.unlinkMsg'),
            });
            this.$emit('reloadAnalysis');
        },
        addWorkOrderTypes() {
            this.$root.$emit('showModal', {
                title: this.$t('generic.workOrderSelection'),
                payload: { analysisConfigurationId: this.analysis.id },
                component: Vue.component('AnalysisWorkOrderTypesForm', AnalysisWorkOrderTypesForm),
                onConfirm: requestPayload => this.addWorkOrderTypesToAnalysis(requestPayload),
            });
        },
        updateAnalysis(analysis) {
            this.mutableAnalysis = { ...this.mutableAnalysis, ...analysis };
            this.$emit('reloadAnalysis');
        },
        async getComponentConfiguration() {
            const { data: { probe } } = await this.$rq.getComponentConfiguration(this.analysis.id);
            this.probeId = probe?.probeId;
        },
        isObjectEmpty(obj) {
            return isEmpty(obj);
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/utils/_variables.scss";

.card {
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 2px 4px #ccc;

    &Header {
        box-shadow: 0 2px 4px #ccc;
        background-color: $primary-color;
        height: 3.5rem;
        display: flex;
        padding: 0 1.5rem;
        align-items: center;
        justify-content: space-between;

        &Title {
            color: white;
            font-size: 18px;
        }
    }

    &Content {
        display: grid;
        grid-template-rows: 1fr 0.5fr;
        height: 10rem;

        &Body {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: .5rem 1.5rem;
            gap: 0.8rem;

            &Left {
                display: flex;
                justify-content: left;
                gap: 0.8rem;
                align-items: center;

                ::v-deep .v-text-field {
                    margin-top: 0;
                }
            }

            &Right {
                text-align: center;
                flex-basis: 120px;
            }
        }
    }
}
</style>
